import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  BarChart, Bar, Area, AreaChart,
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import {
  getRevenueTrend,
  getRevenueByCurrency,
  getPlanDistribution,
  getArpuTrend,
  getCreatorRevenueOverview,
} from '../../services/adminService';
import './AdminRevenuePage.css';

/* ── Types ──────────────────────────────────────────────── */
interface TrendPoint {
  period: string;
  total: number;
  transactionCount: number;
}

interface CurrencyBreakdown {
  currency: string;
  total: number;
  count: number;
}

interface PlanRow {
  billingPeriod: string;
  count: number;
  monthlyRevenue: number;
}

interface ArpuPoint {
  month: string;
  arpu: number;
  activeUsers: number;
  revenue: number;
}

interface CreatorTierRow {
  tier: string;
  creatorCount: number;
  totalRevenue: number;
  pendingPayouts: number;
  totalPaidOut: number;
  avgRating: number;
}

type TabId = 'trend' | 'currency' | 'plans' | 'arpu' | 'creators';

const TABS: { id: TabId; labelKey: string; fallback: string }[] = [
  { id: 'trend', labelKey: 'revenue.tabTrend', fallback: 'Revenue Trend' },
  { id: 'currency', labelKey: 'revenue.tabCurrency', fallback: 'By Currency' },
  { id: 'plans', labelKey: 'revenue.tabPlans', fallback: 'Plan Distribution' },
  { id: 'arpu', labelKey: 'revenue.tabArpu', fallback: 'ARPU Trend' },
  { id: 'creators', labelKey: 'revenue.tabCreators', fallback: 'Creator Revenue' },
];

const PIE_COLORS = ['#667eea', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

const TIER_STYLES: Record<string, { bg: string; border: string; accent: string }> = {
  newbie: { bg: '#f8fafc', border: '#e2e8f0', accent: '#64748b' },
  growth: { bg: '#eff6ff', border: '#bfdbfe', accent: '#2563eb' },
  star: { bg: '#fffbeb', border: '#fde68a', accent: '#d97706' },
};

/* ── Helpers ────────────────────────────────────────────── */
const fmtCurrency = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ── Component ──────────────────────────────────────────── */
const AdminRevenuePage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState<TabId>('trend');

  return (
    <div className="admin-rev-page">
      <h1 className="admin-rev-title">{t('pages.revenue', 'Revenue Analytics')}</h1>

      {/* Tab bar */}
      <div className="admin-rev-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`admin-rev-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {t(tab.labelKey, tab.fallback)}
          </button>
        ))}
      </div>

      <div className="admin-rev-content">
        {activeTab === 'trend' && <TrendTab />}
        {activeTab === 'currency' && <CurrencyTab />}
        {activeTab === 'plans' && <PlansTab />}
        {activeTab === 'arpu' && <ArpuTab />}
        {activeTab === 'creators' && <CreatorsTab />}
      </div>
    </div>
  );
};

/* ── Tab 1: Revenue Trend ───────────────────────────────── */
const TrendTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [granularity, setGranularity] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: { granularity: string; dateFrom?: string; dateTo?: string } = { granularity };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await getRevenueTrend(params);
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [granularity, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="admin-rev-card">
      <div className="admin-rev-card-controls">
        <div className="admin-rev-btn-group">
          {(['daily', 'weekly', 'monthly'] as const).map(g => (
            <button
              key={g}
              className={`admin-rev-btn-group-item ${granularity === g ? 'active' : ''}`}
              onClick={() => setGranularity(g)}
            >
              {t(`revenue.${g}`, g.charAt(0).toUpperCase() + g.slice(1))}
            </button>
          ))}
        </div>
        <div className="admin-rev-date-range">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="admin-rev-date" />
          <span className="admin-rev-date-sep">-</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="admin-rev-date" />
        </div>
      </div>

      {loading ? <Spinner /> : data.length === 0 ? <EmptyState /> : (
        <ResponsiveContainer width="100%" height={360}>
          <AreaChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 16 }}>
            <defs>
              <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#667eea" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#667eea" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `$${v}`} />
            <Tooltip
              contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }}
              formatter={(value: unknown) => [fmtCurrency(Number(value)), t('revenue.revenue', 'Revenue')]}
              labelFormatter={(label) => String(label)}
            />
            <Area type="monotone" dataKey="total" stroke="#667eea" strokeWidth={2} fill="url(#trendFill)" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

/* ── Tab 2: By Currency ─────────────────────────────────── */
const CurrencyTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState<CurrencyBreakdown[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: { dateFrom?: string; dateTo?: string } = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const res = await getRevenueByCurrency(params);
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const grandTotal = data.reduce((s, d) => s + d.total, 0);

  return (
    <div className="admin-rev-card">
      <div className="admin-rev-card-controls">
        <div className="admin-rev-date-range">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="admin-rev-date" />
          <span className="admin-rev-date-sep">-</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="admin-rev-date" />
        </div>
      </div>

      {loading ? <Spinner /> : data.length === 0 ? <EmptyState /> : (
        <>
          <div className="admin-rev-pie-container">
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="total"
                  nameKey="currency"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  innerRadius={60}
                  paddingAngle={2}
                  label={(props: PieLabelRenderProps) => `${props.name || ''} ${((Number(props.percent)) * 100).toFixed(1)}%`}
                  labelLine={false}
                >
                  {data.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }}
                  formatter={(value: unknown) => [fmtCurrency(Number(value))]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <table className="admin-rev-currency-table">
            <thead>
              <tr>
                <th>{t('revenue.currency', 'Currency')}</th>
                <th>{t('revenue.total', 'Total')}</th>
                <th>{t('revenue.txCount', 'Transactions')}</th>
                <th>{t('revenue.percentage', '%')}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={row.currency}>
                  <td>
                    <span className="admin-rev-currency-dot" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    {row.currency}
                  </td>
                  <td className="admin-rev-num">{fmtCurrency(row.total)}</td>
                  <td className="admin-rev-num">{row.count.toLocaleString()}</td>
                  <td className="admin-rev-num">{grandTotal > 0 ? ((row.total / grandTotal) * 100).toFixed(1) : '0.0'}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

/* ── Tab 3: Plan Distribution ───────────────────────────── */
const PlansTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getPlanDistribution()
      .then(res => setData(res.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  const totalMRR = data.reduce((s, d) => s + d.monthlyRevenue, 0);
  const totalSubs = data.reduce((s, d) => s + d.count, 0);

  return (
    <div className="admin-rev-card">
      {loading ? <Spinner /> : data.length === 0 ? <EmptyState /> : (
        <>
          {/* Big metric cards */}
          <div className="admin-rev-plan-cards">
            {data.map(row => {
              const pct = totalSubs > 0 ? ((row.count / totalSubs) * 100).toFixed(1) : '0';
              return (
                <div key={row.billingPeriod} className="admin-rev-plan-card">
                  <div className="admin-rev-plan-label">
                    {row.billingPeriod === 'monthly'
                      ? t('revenue.monthlyPlan', 'Monthly')
                      : t('revenue.yearlyPlan', 'Yearly')}
                  </div>
                  <div className="admin-rev-plan-count">{row.count.toLocaleString()}</div>
                  <div className="admin-rev-plan-sub">{t('revenue.subscribers', 'subscribers')}</div>
                  <div className="admin-rev-plan-mrr">{fmtCurrency(row.monthlyRevenue)} MRR</div>
                  <div className="admin-rev-plan-pct">{pct}%</div>
                </div>
              );
            })}
          </div>

          {/* Bar chart */}
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={{ top: 16, right: 24, bottom: 8, left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="billingPeriod"
                tick={{ fontSize: 13, fill: '#64748b' }}
                tickFormatter={(v: string) => v.charAt(0).toUpperCase() + v.slice(1)}
              />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `$${v}`} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }}
                formatter={(value: unknown) => [fmtCurrency(Number(value)), 'MRR']}
              />
              <Bar dataKey="monthlyRevenue" fill="#667eea" radius={[8, 8, 0, 0]} barSize={60} />
            </BarChart>
          </ResponsiveContainer>

          <div className="admin-rev-plan-total">
            {t('revenue.totalMRR', 'Total MRR')}: <strong>{fmtCurrency(totalMRR)}</strong>
          </div>
        </>
      )}
    </div>
  );
};

/* ── Tab 4: ARPU Trend ──────────────────────────────────── */
const ArpuTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [months, setMonths] = useState(12);
  const [data, setData] = useState<ArpuPoint[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getArpuTrend(months)
      .then(res => setData(res.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [months]);

  return (
    <div className="admin-rev-card">
      <div className="admin-rev-card-controls">
        <div className="admin-rev-btn-group">
          {[6, 12, 24].map(m => (
            <button
              key={m}
              className={`admin-rev-btn-group-item ${months === m ? 'active' : ''}`}
              onClick={() => setMonths(m)}
            >
              {m}m
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : data.length === 0 ? <EmptyState /> : (
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
            <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `$${v}`} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#94a3b8' }} />
            <Tooltip
              contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13 }}
              formatter={(value: unknown, name: unknown) => {
                const v = Number(value);
                const n = String(name);
                if (n === 'arpu') return [fmtCurrency(v), 'ARPU'];
                if (n === 'activeUsers') return [v.toLocaleString(), t('revenue.activeUsers', 'Active Users')];
                return [fmtCurrency(v), t('revenue.revenue', 'Revenue')];
              }}
            />
            <Line yAxisId="left" type="monotone" dataKey="arpu" stroke="#667eea" strokeWidth={2} dot={{ r: 4, fill: '#667eea' }} />
            <Line yAxisId="right" type="monotone" dataKey="activeUsers" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      )}

      <div className="admin-rev-arpu-legend">
        <span className="admin-rev-legend-item">
          <span className="admin-rev-legend-line" style={{ background: '#667eea' }} />
          ARPU ($)
        </span>
        <span className="admin-rev-legend-item">
          <span className="admin-rev-legend-line admin-rev-legend-dashed" style={{ background: '#94a3b8' }} />
          {t('revenue.activeUsers', 'Active Users')}
        </span>
      </div>
    </div>
  );
};

/* ── Tab 5: Creator Revenue ─────────────────────────────── */
const CreatorsTab: React.FC = () => {
  const { t } = useTranslation('admin');
  const [data, setData] = useState<CreatorTierRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getCreatorRevenueOverview()
      .then(res => setData(res.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  const tierOrder = ['newbie', 'growth', 'star'];
  const sorted = [...data].sort((a, b) => tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier));

  return (
    <div className="admin-rev-card">
      {loading ? <Spinner /> : data.length === 0 ? <EmptyState /> : (
        <div className="admin-rev-creator-grid">
          {sorted.map(row => {
            const style = TIER_STYLES[row.tier] || TIER_STYLES.newbie;
            return (
              <div
                key={row.tier}
                className="admin-rev-creator-card"
                style={{ background: style.bg, borderColor: style.border }}
              >
                <div className="admin-rev-creator-tier" style={{ color: style.accent }}>
                  {/* Tier icon */}
                  {row.tier === 'star' ? (
                    <svg viewBox="0 0 24 24" fill={style.accent} width="20" height="20">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ) : row.tier === 'growth' ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke={style.accent} strokeWidth="2" width="20" height="20">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                      <polyline points="17 6 23 6 23 12" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke={style.accent} strokeWidth="2" width="20" height="20">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4M12 8h.01" />
                    </svg>
                  )}
                  <span className="admin-rev-creator-tier-name">
                    {row.tier.charAt(0).toUpperCase() + row.tier.slice(1)}
                  </span>
                </div>

                <div className="admin-rev-creator-metrics">
                  <div className="admin-rev-creator-metric">
                    <span className="admin-rev-creator-metric-val">{row.creatorCount}</span>
                    <span className="admin-rev-creator-metric-lbl">{t('revenue.creators', 'Creators')}</span>
                  </div>
                  <div className="admin-rev-creator-metric">
                    <span className="admin-rev-creator-metric-val">{fmtCurrency(row.totalRevenue)}</span>
                    <span className="admin-rev-creator-metric-lbl">{t('revenue.totalRevenue', 'Total Revenue')}</span>
                  </div>
                  <div className="admin-rev-creator-metric">
                    <span className="admin-rev-creator-metric-val">{fmtCurrency(row.pendingPayouts)}</span>
                    <span className="admin-rev-creator-metric-lbl">{t('revenue.pending', 'Pending Payouts')}</span>
                  </div>
                  <div className="admin-rev-creator-metric">
                    <span className="admin-rev-creator-metric-val">{fmtCurrency(row.totalPaidOut)}</span>
                    <span className="admin-rev-creator-metric-lbl">{t('revenue.paidOut', 'Paid Out')}</span>
                  </div>
                  <div className="admin-rev-creator-metric">
                    <span className="admin-rev-creator-metric-val">
                      {row.avgRating > 0 ? (
                        <>
                          <svg viewBox="0 0 24 24" fill="#f59e0b" width="14" height="14" style={{ verticalAlign: '-1px', marginRight: 2 }}>
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                          {row.avgRating.toFixed(1)}
                        </>
                      ) : '-'}
                    </span>
                    <span className="admin-rev-creator-metric-lbl">{t('revenue.avgRating', 'Avg Rating')}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Shared sub-components ──────────────────────────────── */
const Spinner: React.FC = () => (
  <div className="admin-rev-loading">
    <div className="admin-rev-spinner" />
  </div>
);

const EmptyState: React.FC = () => {
  const { t } = useTranslation('admin');
  return (
    <div className="admin-rev-empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="40" height="40">
        <path d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <p>{t('revenue.noData', 'No data available.')}</p>
    </div>
  );
};

export default AdminRevenuePage;
